<template>
  <div class="intl-tel-input-wrapper">
    <label v-if="label" :for="id">{{ label }}</label>
    <input
      :id="id"
      ref="phoneInput"
      type="tel"
      :placeholder="placeholder"
      :disabled="disabled"
      @keyup.enter="$emit('enter')"
    />
    <p v-if="shouldShowValidationMessage" class="validation">
      {{ internalValidationMessage }}
    </p>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import i18n from '@/plugins/i18n'
import intlTelInput from 'intl-tel-input'
import 'intl-tel-input/build/css/intlTelInput.css'
import '@/assets/css/Elements/IntlTelInput.css'

export default {
  name: 'IntlTelInput',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: 'phone',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: () => i18n.global.t('intlTelInput.defaultPlaceholder'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    initialCountry: {
      type: String,
      default: 'us',
    },
    preferredCountries: {
      type: Array,
      default: () => ['us', 'ca', 'gb'],
    },
    onlyCountries: {
      type: Array,
      default: () => [],
    },
    excludeCountries: {
      type: Array,
      default: () => [],
    },
    validationNumberTypes: {
      type: Array,
      default: () => ['MOBILE'],
    },
    useValidation: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iti: null,
      handleInputBound: null,
      handleBlurBound: null,
      handleCountryChangeBound: null,
      touched: false,
      internalValidationMessage: '',
      lastEmittedValidity: null,
      utilsLoaded: false,
      validationTimeout: null,
    }
  },
  computed: {
    shouldShowValidationMessage() {
      return this.useValidation && this.touched && !!this.internalValidationMessage
    },
  },
  mounted() {
    this.initializeIntlTelInput()
  },
  beforeUnmount() {
    // Clear validation timeout
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout)
    }

    if (this.iti) {
      const input = this.$refs.phoneInput
      if (input && this.handleInputBound) {
        input.removeEventListener('input', this.handleInputBound)
      }
      if (input && this.handleBlurBound) {
        input.removeEventListener('blur', this.handleBlurBound)
      }
      if (input && this.handleCountryChangeBound) {
        input.removeEventListener('countrychange', this.handleCountryChangeBound)
      }
      this.iti.destroy()
    }
  },
  watch: {
    modelValue(newValue) {
      if (this.iti && newValue !== this.iti.getNumber()) {
        this.iti.setNumber(newValue)
      }
    },
  },
  methods: {
    initializeIntlTelInput() {
      const input = this.$refs.phoneInput

      const options = {
        initialCountry: this.initialCountry,
        preferredCountries: this.preferredCountries,
        separateDialCode: true,
        formatOnDisplay: true,
        formatAsYouType: true,
        strictMode: true, // Only allow numeric characters and optional + at the beginning
        autoPlaceholder: 'polite',
        nationalMode: false,
        showFlags: true,
        countrySearch: true,
        useFullscreenPopup: false,
        loadUtils: () => import('intl-tel-input/utils'),
        validationNumberTypes: this.validationNumberTypes,
      }

      if (this.onlyCountries.length > 0) {
        options.onlyCountries = this.onlyCountries
      }

      if (this.excludeCountries.length > 0) {
        options.excludeCountries = this.excludeCountries
      }

      this.iti = intlTelInput(input, options)
      if (this.iti && this.iti.promise) {
        this.iti.promise.then(() => {
          this.utilsLoaded = true
          if (this.useValidation) {
            this.validate(false)
          }
        })
      } else {
        this.utilsLoaded = true
      }

      // Set initial value if provided
      if (this.modelValue) {
        this.iti.setNumber(this.modelValue)
        if (this.useValidation) {
          this.validate(false)
        }
      }

      // Listen for input changes (using bound method for cleanup)
      this.handleInputBound = this.handleInput.bind(this)
      this.handleBlurBound = this.handleBlur.bind(this)
      this.handleCountryChangeBound = this.handleCountryChange.bind(this)

      input.addEventListener('input', this.handleInputBound)
      input.addEventListener('blur', this.handleBlurBound)
      input.addEventListener('countrychange', this.handleCountryChangeBound)
    },
    handleInput() {
      // Use requestAnimationFrame to ensure intl-tel-input has finished formatting
      requestAnimationFrame(() => {
        const valueToEmit = this.getValueToUse()
        // Only emit if value actually changed to avoid infinite loops
        if (valueToEmit !== this.modelValue) {
          this.$emit('update:modelValue', valueToEmit)
          this.$emit('input', valueToEmit)
        }
        if (this.useValidation) {
          this.touched = true
          // Debounce validation to avoid excessive calls
          if (this.validationTimeout) {
            clearTimeout(this.validationTimeout)
          }
          this.validationTimeout = setTimeout(() => {
            this.validate(true, true)
          }, 300)
        }
      })
    },
    handleBlur() {
      if (this.useValidation) {
        this.touched = true
        this.validate(true, true)
      }
    },
    handleCountryChange() {
      const countryData = this.getSelectedCountryData()
      this.$emit('countryChange', countryData)
      if (this.useValidation) {
        // User changed country, mark as touched
        this.touched = true
        this.validate(true, true)
      }
    },
    getNumber() {
      return this.iti ? this.iti.getNumber() : ''
    },
    /**
     * Get the national number (without country code)
     * Useful when you need to send just the national number to your API
     * @returns {string} National number without country code
     */
    getNationalNumber() {
      if (!this.iti) return ''
      try {
        const input = this.$refs.phoneInput
        if (!input || !input.value) return ''

        // Prefer E.164 when available, but don't depend on utils being loaded yet.
        const intlNumber = this.getValueToUse()
        if (!intlNumber) return ''

        // Get country data to extract dial code
        const countryData = this.getSelectedCountryData()
        if (!countryData || !countryData.dialCode) return ''

        // Extract digits from E.164 format (remove + and any formatting)
        const digitsOnly = intlNumber.replace(/\D/g, '')
        
        // Remove country code from the beginning
        // This gives us the correct national number without leading zeros
        if (digitsOnly.startsWith(countryData.dialCode)) {
          return digitsOnly.substring(countryData.dialCode.length)
        }

        // Fallback: if E.164 doesn't start with dial code, try extracting from input
        const rawValue = input.value.trim()
        const nationalNumber = rawValue.replace(/\D/g, '')
        return nationalNumber
      } catch (error) {
        console.warn('Error getting national number:', error)
        return ''
      }
    },
    isValidNumber() {
      return this.iti ? this.iti.isValidNumber() : false
    },
    getValidationError() {
      return this.iti ? this.iti.getValidationError() : null
    },
    setNumber(number) {
      if (this.iti) {
        this.iti.setNumber(number)
      }
    },
    setCountry(countryCode) {
      if (this.iti && countryCode) {
        this.iti.setCountry(countryCode.toLowerCase())
      }
    },
    getSelectedCountryData() {
      return this.iti ? this.iti.getSelectedCountryData() : null
    },
    setTouched(value = true) {
      this.touched = value
    },
    validate(emitEvent = true, markAsTouched = false) {
      if (markAsTouched) {
        this.touched = true
      }

      let isValid = true
      let message = ''
      const valueToCheck = this.getValueToUse()

      // Check if required field is empty
      if (this.required && !valueToCheck) {
        isValid = false
        message = this.t('intlTelInput.errors.validPhone')
        this.internalValidationMessage = message
        if (emitEvent) {
          this.emitValidityChange(false, message, '')
        }
        return false
      }

      // If no value and not required, skip validation
      if (!valueToCheck) {
        this.internalValidationMessage = ''
        if (emitEvent) {
          this.emitValidityChange(true, '', '')
        }
        return true
      }

      // Validate using intl-tel-input library validation (country-specific)
      if (this.utilsLoaded && this.iti) {
        try {
          const hasPreciseValidation = typeof this.iti.isValidNumberPrecise === 'function'
          const isValidNumber = hasPreciseValidation ? this.iti.isValidNumberPrecise() : this.iti.isValidNumber()

          // Utils loaded - use library's country-specific validation
          // Check if validation methods are available (not null/undefined)
          if (isValidNumber !== null && isValidNumber !== undefined) {
            // Library validation is available - trust it completely
            // It handles each country's specific format and length rules
            if (!isValidNumber) {
              isValid = false
              message = this.mapValidationError()
            }
          } else {
            // Utils script not fully loaded yet or validation unavailable - minimal fallback
            const digitsOnly = valueToCheck.replace(/\D/g, '')
            if (digitsOnly.length < 8) {
              isValid = false
              message = this.t('intlTelInput.errors.tooShort')
            }
          }
        } catch (error) {
          // Error accessing validation methods - fallback validation
          console.warn('Error validating phone number:', error)
          const digitsOnly = valueToCheck.replace(/\D/g, '')
          if (digitsOnly.length < 8) {
            isValid = false
            message = this.t('intlTelInput.errors.tooShort')
          }
        }
      } else if (this.required) {
        // Utils not loaded yet - minimal validation only
        const digitsOnly = valueToCheck.replace(/\D/g, '')
        if (digitsOnly.length < 8) {
          isValid = false
          message = this.t('intlTelInput.errors.tooShort')
        }
      }

      this.internalValidationMessage = message

      if (emitEvent) {
        this.emitValidityChange(isValid, message, valueToCheck)
      }

      return isValid
    },
    getValueToUse() {
      if (!this.iti) return ''

      const input = this.$refs.phoneInput
      if (!input) return ''

      // Try to get the formatted international number from the library
      // This is the recommended way - getNumber() returns E.164 format (e.g., +17024181234)
      try {
        const intlNumber = this.iti.getNumber()

        // If library gives us a valid international number, use it
        // getNumber() returns empty string or null if invalid/empty
        if (intlNumber && intlNumber.trim() && intlNumber.trim() !== '+') {
          return intlNumber.trim()
        }
      } catch (error) {
        // If getNumber() throws an error (e.g., utils not loaded), fall back to manual construction
        console.warn('Error getting number from intl-tel-input:', error)
      }

      // Fallback: If getNumber() returns nothing or throws, construct from input value
      const rawValue = input.value ? input.value.trim() : ''
      if (!rawValue) return ''

      // Extract only digits
      const digitsOnly = rawValue.replace(/\D/g, '')
      if (!digitsOnly) return ''

      // Get selected country data
      const countryData = this.getSelectedCountryData()
      if (!countryData || !countryData.dialCode) return ''

      // If input already starts with country code, return as is with +
      if (digitsOnly.startsWith(countryData.dialCode)) {
        return `+${digitsOnly}`
      }

      // Otherwise, prepend country code
      return `+${countryData.dialCode}${digitsOnly}`
    },
    emitValidityChange(isValid, message, value) {
      const payload = {
        isValid,
        message,
        value,
        countryData: this.getSelectedCountryData(),
      }
      // Avoid emitting duplicate consecutive states
      if (JSON.stringify(this.lastEmittedValidity) !== JSON.stringify(payload)) {
        this.lastEmittedValidity = payload
        this.$emit('validityChange', payload)
      }
    },
    mapValidationError() {
      if (!this.iti) return this.t('intlTelInput.errors.validPhone')

      const errorCode = this.iti.getValidationError()

      // Map libphonenumber validation error codes
      // These correspond to intlTelInput.utils.validationError enum
      const errorMessages = {
        0: this.t('intlTelInput.errors.invalidPhoneNumber'), // IS_POSSIBLE (but invalid)
        1: this.t('intlTelInput.errors.invalidCountryCode'), // INVALID_COUNTRY_CODE
        2: this.t('intlTelInput.errors.tooShort'), // TOO_SHORT
        3: this.t('intlTelInput.errors.tooLong'), // TOO_LONG
        4: this.t('intlTelInput.errors.invalidLength'), // INVALID_LENGTH
        // Additional error codes that might be returned
        '-99': this.t('intlTelInput.errors.validPhone'), // Utils not loaded or invalid state
      }

      // Handle null/undefined error codes
      if (errorCode === null || errorCode === undefined) {
        return this.t('intlTelInput.errors.validPhone')
      }

      return errorMessages[errorCode] || errorMessages[String(errorCode)] || this.t('intlTelInput.errors.validPhone')
    },
  },
}
</script>
